import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../services/api';
import { Reference } from '../types';

const References: React.FC = () => {
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newReference, setNewReference] = useState('');
  const [newType, setNewType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [referenceTypes, setReferenceTypes] = useState<string[]>([]);

  const fetchReferences = async () => {
    setLoading(true);
    try {
      const res = await api.getReferences({ active: 'all' });
      setReferences(res.data?.references || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load references', severity: 'error' });
    }
    setLoading(false);
  };

  const fetchReferenceTypes = async () => {
    try {
      const res = await api.getReferenceTypes();
      setReferenceTypes(res.data?.types || []);
    } catch (err) {
      setReferenceTypes([]);
    }
  };

  useEffect(() => {
    fetchReferences();
    fetchReferenceTypes();
  }, []);

  const handleAddReference = async () => {
    if (!newReference.trim() || !newType.trim()) {
      setSnackbar({ open: true, message: 'Reference and type are required', severity: 'error' });
      return;
    }
    try {
      await api.createReference({ Reference: newReference, Type: newType });
      setSnackbar({ open: true, message: 'Reference added successfully', severity: 'success' });
      setOpenAddDialog(false);
      setNewReference('');
      setNewType('');
      fetchReferences();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add reference', severity: 'error' });
    }
  };

  const handleDeleteReference = async (id: number) => {
    setDeletingId(id);
    try {
      await api.deleteReference(id);
      setSnackbar({ open: true, message: 'Reference deleted successfully', severity: 'success' });
      fetchReferences();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete reference', severity: 'error' });
    }
    setDeletingId(null);
    setConfirmDelete({ open: false, id: null });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          References
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}>
          Add Reference
        </Button>
      </Box>
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {references.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No references found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    references.map((reference, idx) => (
                      <TableRow key={reference.ID}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{reference.Reference}</TableCell>
                        <TableCell>{reference.Type}</TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => setConfirmDelete({ open: true, id: reference.ID })}
                            disabled={deletingId === reference.ID}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Reference Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Reference</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reference"
            fullWidth
            value={newReference}
            onChange={(e) => setNewReference(e.target.value)}
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }} required>
            <InputLabel>Type</InputLabel>
            <Select
              value={newType}
              label="Type"
              onChange={(e) => setNewType(e.target.value)}
            >
              {referenceTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddReference} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
        <DialogTitle>Delete Reference</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this reference?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })}>Cancel</Button>
          <Button
            onClick={() => confirmDelete.id && handleDeleteReference(confirmDelete.id)}
            color="error"
            variant="contained"
            disabled={deletingId === confirmDelete.id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default References; 